// Wrestling club payment fee breakdown.
//
// Pure functions, no Vue / DOM dependencies, so they can be unit tested
// without mounting a component and reused elsewhere. These mirror the
// standard published WrestlingIQ billing defaults (not per-team overrides):
//
//   WIQ platform fee  : 2%  of the base price (Team#wiq_percent_fee = 0.02),
//                       added ON TOP of the parent's bill (grossed up).
//   Card processing   : 2.9% + $0.30 (Team defaults cc_basis_point_rate = 290,
//                       fixed_transaction_fee = 30 cents), applied to the total
//                       amount actually charged to the parent — i.e. the base
//                       price PLUS the WIQ fee, matching BillingProfile#processing_fee.
//
// IMPORTANT: the app works in integer cents and rounds at EACH step
// (`(amount * rate + fixed).round` per BillingProfile#processing_fee and the
// wiq-fee line item). We do the same here rather than carrying floats through,
// so every displayed line item reconciles to the penny with the real bill —
// otherwise "base + WIQ fee" could disagree with "parent pays" by a cent.
// Math.round matches Ruby's Float#round for the non-negative amounts here
// (both round halves up / away from zero for positives).

export const WIQ_FEE_RATE = 0.02; // 2% platform fee, added to the parent bill
export const PROCESSING_RATE = 0.029; // 2.9% card processing rate
export const PROCESSING_FIXED_CENTS = 30; // $0.30 fixed per-transaction fee

const toCents = (dollars) => Math.round(dollars * 100);
const toDollars = (cents) => cents / 100;

// The WrestlingIQ platform fee in cents: 2% of the base price, rounded.
function wiqFeeCents(baseCents) {
  return Math.round(baseCents * WIQ_FEE_RATE);
}

// What the parent is actually charged, in cents: base price + the WIQ fee.
function parentTotalCents(baseCents) {
  return baseCents + wiqFeeCents(baseCents);
}

// Card processing fee in cents: 2.9% + $0.30, charged on the total the parent
// pays (base + WIQ fee), not on the base alone. Rounded like the app.
function processingFeeCents(baseCents) {
  return Math.round(parentTotalCents(baseCents) * PROCESSING_RATE + PROCESSING_FIXED_CENTS);
}

// What actually lands in the club's bank account, in cents. The parent pays
// parentTotal; the WIQ fee is passed through to WrestlingIQ and the processing
// fee goes to the card processor, so the club keeps base - processingFee.
function netToClubCents(baseCents) {
  return baseCents - processingFeeCents(baseCents);
}

// Dollar-returning helpers (rounded to the cent) for direct use / testing.
export function wiqFee(basePrice) {
  return toDollars(wiqFeeCents(toCents(basePrice)));
}

export function parentTotal(basePrice) {
  return toDollars(parentTotalCents(toCents(basePrice)));
}

export function processingFee(basePrice) {
  return toDollars(processingFeeCents(toCents(basePrice)));
}

export function netToClub(basePrice) {
  return toDollars(netToClubCents(toCents(basePrice)));
}

// The full breakdown for a given base price, in dollars, each value rounded to
// the cent so the line items reconcile. A non-positive or non-numeric base
// price yields an all-zero breakdown (rather than a lone $0.30 fixed fee with a
// negative net) so the empty-input state reads cleanly.
export function feeBreakdown(basePrice) {
  const price = Number.isFinite(basePrice) && basePrice > 0 ? basePrice : 0;
  // Normalize to cents before the zero check: a sub-cent price (e.g. $0.003)
  // is > 0 but rounds to 0 cents, which would otherwise yield a lone $0.30
  // fixed fee and a negative net on a displayed $0.00 base.
  const baseCents = toCents(price);
  if (baseCents === 0) {
    return {
      basePrice: 0,
      wiqFee: 0,
      parentTotal: 0,
      processingFee: 0,
      netToClub: 0,
    };
  }
  return {
    basePrice: toDollars(baseCents),
    wiqFee: toDollars(wiqFeeCents(baseCents)),
    parentTotal: toDollars(parentTotalCents(baseCents)),
    processingFee: toDollars(processingFeeCents(baseCents)),
    netToClub: toDollars(netToClubCents(baseCents)),
  };
}
